import { memo, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  CalendarDaysIcon,
  Cog6ToothIcon,
  ArrowPathIcon,
} from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";
import { getBaseUrl } from "../api/api";

interface CalendarToolbarProps {
  currentView: "dayGridMonth" | "timeGridWeek" | "timeGridDay";
  onViewChange: (view: "dayGridMonth" | "timeGridWeek" | "timeGridDay") => void;
  onPrev: () => void;
  onNext: () => void;
  onToday: () => void;
  allowedViews?: Array<"dayGridMonth" | "timeGridWeek" | "timeGridDay">;
  hideViewTabs?: boolean;
  calendarHref?: string;
  onCalendarClick?: () => void;
  dateRangeLabel?: string;
  onResyncCalendar?: () => void;
  onRefreshApps?: () => void;
  calendarConnected?: boolean;
  calendarConnectedEmail?: string;
  appsSettingsHref?: string;
}

const CalendarToolbar = memo<CalendarToolbarProps>(
  ({
    currentView,
    onViewChange,
    onPrev,
    onNext,
    onToday,
    allowedViews,
    hideViewTabs,
    calendarHref,
    onCalendarClick,
    dateRangeLabel,
    onRefreshApps,
    calendarConnected,
    calendarConnectedEmail,
    appsSettingsHref,
  }) => {
    const allViewOptions = [
      { key: "dayGridMonth" as const, label: "Month" },
      { key: "timeGridWeek" as const, label: "Week" },
      { key: "timeGridDay" as const, label: "Day" },
    ];

    const { t } = useTranslation();

    const viewOptions =
      allowedViews && allowedViews.length > 0
        ? allViewOptions.filter((v) => allowedViews.includes(v.key))
        : allViewOptions;

    const shouldShowViewTabs = !hideViewTabs && viewOptions.length > 1;

    const navigate = useNavigate();

    const [isSettingsOpen, setIsSettingsOpen] = useState(false);
    const settingsRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      const handler = (e: MouseEvent) => {
        if (settingsRef.current && !settingsRef.current.contains(e.target as Node)) {
          setIsSettingsOpen(false);
        }
      };
      if (isSettingsOpen) {
        document.addEventListener("mousedown", handler);
      }
      return () => document.removeEventListener("mousedown", handler);
    }, [isSettingsOpen]);

    const openSettings = () => {
      onRefreshApps?.();
      setIsSettingsOpen((v) => !v);
    };

    return (
      <div className="flex items-center justify-between mb-6">
        <div className="flex flex-col">
          <span className="font-medium text-gray-600 truncate">{dateRangeLabel}</span>
        </div>

        {shouldShowViewTabs && (
          <div className="flex items-center space-x-2">
            <div className="flex bg-gray-100 rounded-lg p-1">
              {viewOptions.map((view) => (
                <button
                  key={view.key}
                  onClick={() => onViewChange(view.key)}
                  className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors w-[80px] text-center ${
                    currentView === view.key
                      ? "bg-white text-gray-900 shadow-sm"
                      : "text-gray-600 hover:text-gray-900"
                  }`}
                >
                  {view.label}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="flex items-center space-x-1">
          <button
            onClick={onPrev}
            className="p-2 rounded-lg bg-white border border-white shadow-sm hover:bg-gray-50 transition-colors"
            aria-label="Previous"
          >
            <ChevronLeftIcon className="w-5 h-5 text-gray-600" />
          </button>
          <button
            onClick={onToday}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-white shadow-sm hover:bg-gray-50 rounded-lg transition-colors"
          >
            Today
          </button>
          <button
            onClick={onNext}
            className="p-2 rounded-lg bg-white border border-white shadow-sm hover:bg-gray-50 transition-colors"
            aria-label="Next"
          >
            <ChevronRightIcon className="w-5 h-5 text-gray-600" />
          </button>
          {onCalendarClick && calendarHref && (
            <Link
              to={calendarHref}
              onClick={onCalendarClick}
              className="ml-1 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-white shadow-sm hover:bg-gray-50 rounded-lg transition-colors flex items-center space-x-2"
              aria-label="Open Calendar"
              title="Open Calendar"
            >
              <CalendarDaysIcon className="w-5 h-5 text-gray-600" />
              <span className="hidden sm:inline">View All</span>
            </Link>
          )}

          <div className="ml-2 relative" ref={settingsRef}>
            <button
              onClick={openSettings}
              className="p-2 rounded-lg bg-white border border-white shadow-sm hover:bg-gray-50 transition-colors"
              title={
                calendarConnectedEmail
                  ? `Connected as ${calendarConnectedEmail}`
                  : "Calendar settings"
              }
            >
              <Cog6ToothIcon className="w-5 h-5 text-gray-600" />
            </button>
            {isSettingsOpen && (
              <div className="absolute right-0 mt-2 w-80 bg-white rounded-xl shadow-2xl border border-gray-200 z-50">
                <div className="px-4 py-3 border-b border-gray-200 text-xs font-semibold text-gray-700">
                  {calendarConnected ? t("CONNECTED CALENDAR") : t("CALENDAR")}
                </div>
                <div className="px-4 py-3 text-sm text-gray-700">
                  <div className="text-gray-700">
                    Connected calendar impacts your availability. To modify go to{" "}
                    <Link
                      to={appsSettingsHref}
                      onClick={onCalendarClick}
                      className="text-blue-600 hover:underline"
                    >
                      calendar settings
                    </Link>
                  </div>
                </div>
                <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
                  <div>
                    <div className="flex items-center space-x-2 text-sm text-gray-800">
                      <img
                        src={`${getBaseUrl()}/static/images/google.png`}
                        className="w-4 h-4"
                        alt="Google"
                      />
                      <span>
                        {calendarConnected && calendarConnectedEmail
                          ? calendarConnectedEmail
                          : "Not connected"}
                      </span>
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      {calendarConnected
                        ? "Google Calendar sync - active"
                        : "Click settings to connect"}
                    </div>
                  </div>
                  {calendarConnected && (
                    <button
                      onClick={() => {
                        navigate(`${appsSettingsHref}&appId=google&item=Calendar`);
                        onCalendarClick?.();
                      }}
                      className="p-2 rounded-lg hover:bg-gray-100"
                      title="Re-sync now"
                    >
                      <ArrowPathIcon className="w-4 h-4 text-gray-600" />
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }
);

CalendarToolbar.displayName = "CalendarToolbar";

export default CalendarToolbar;
